import {Injectable} from '@angular/core';
import {MatDialog, MatDialogConfig, MatDialogRef} from '@angular/material/dialog';

import {ScrollStrategy, ScrollStrategyOptions} from '@angular/cdk/overlay';
import {GoogleMapModalComponent} from '../components/maps/components/google-map-modal/google-map-modal.component';
import {AskComponent} from '../components/modals/ask/ask.component';
import {BecomeCustomerFormComponent} from '../components/modals/become-customer-form/become-customer-form.component';
import {BookComponent} from '../components/modals/book/book.component';
import {CustomBookComponent} from '../components/modals/custom-book/custom-book.component';
import {CustomerRateComponent} from '../components/modals/customer-rate/customer-rate.component';
import {DoktorseReceptComponent} from '../components/modals/doktorse-recept/doktorse-recept.component';
import {InfoModalComponent} from '../components/modals/info-modal/info-modal.component';
import {MessageModalComponent} from '../components/modals/message/message-modal.component';
import {RateComponent} from '../components/modals/rate/rate.component';
import {TreatmentDetailsModalComponent} from '../components/modals/treatment-details-modal/treatment-details-modal.component';
import {UpdateInfoFormComponent} from '../components/modals/update-info-form/update-info-form.component';
import {VardenBookingConfirmationV2Component} from '../components/modals/varden-booking-confirmation-v2/varden-booking-confirmation-v2.component';
import {ModalTypeEnum} from '../enums/ModalEnums';
import {BookingResponse} from '../modules/booking-v2/types/booking-response';
import {CareproviderUnitItem} from '../types/CareproviderUnitItem';
import {CustomerFeature} from '../types/CustomerFeature';
import {InfoModalContent} from '../types/InfoModalContent';
import {MapData} from '../types/MapData';
import {ModalCommand} from '../types/ModalCommand';
import {TreatmentDetailsModalData} from '../types/TreatmentDetailsModalData';
import {MessageModalData} from '../types/MessageModalData';
import {ToolsService} from './tools.service';
import {BookingModalData} from '../types/BookingModalData';
import {DeviceTypeEnum} from '../enums/DeviceTypeEnum';
import {DLPageTypeEnum} from '../enums/DataLayerEnums';
import {ImagesCarouselData} from '../types/ImagesCarouselData';
import {ImagesCarouselComponent} from '../components/images-carousel/images-carousel/images-carousel.component';

@Injectable()
export class ModalsControlService {
  private scrollStrategy: ScrollStrategy;
  private openModalFnMap: Array<Function> = [];
  private activeModals: string[] = [];

  constructor(
    public dialog: MatDialog,
    private tools: ToolsService,
    private readonly sso: ScrollStrategyOptions,
  ) {
    this.buildOpenModalFnMap();
  }

  public openModal(modalCommand: ModalCommand): void {
    if (
      !this.tools.isNil(modalCommand) &&
      !this.tools.isNil(this.openModalFnMap[modalCommand.type]) &&
      this.activeModals.indexOf(modalCommand.type) < 0
    ) {
      this.activeModals.push(modalCommand.type);
      this.openModalFnMap[modalCommand.type](modalCommand.data);
    }
  }

  public closeAll(): void {
    this.activeModals = [];
    this.dialog.closeAll();
  }

  private onClose(type: string): void {
    this.activeModals = this.activeModals.filter((item) => item !== type);
  }

  private buildOpenModalFnMap(): void {
    this.openModalFnMap[ModalTypeEnum.RATING] = (modalData) => this.openRatingModal(modalData);
    this.openModalFnMap[ModalTypeEnum.QUESTION] = (modalData) => this.openQuestionModal(modalData);
    this.openModalFnMap[ModalTypeEnum.BOOKING] = (modalData) => this.openBookingModal(modalData);
    this.openModalFnMap[ModalTypeEnum.CUSTOM_BOOKING] = (modalData) => this.openCustomBookingModal(modalData);
    this.openModalFnMap[ModalTypeEnum.UPDATE_INFO] = (modalData) => this.openUpdateInfoModal(modalData);
    this.openModalFnMap[ModalTypeEnum.DOKTORSE_RECEPT] = (modalData) => this.openDoktorseReceptModal(modalData);
    // this.openModalFnMap[ModalTypeEnum.MAILCHIMP] = (modalData) => this.openMailchimpModal();
    this.openModalFnMap[ModalTypeEnum.BECOME_CUSTOMER] = (modalData) => this.openBecomeCustomerModal(modalData);
    // this.openModalFnMap[ModalTypeEnum.LISTA_DIG_HSA] = (modalData) => this.openListaDigHsaModal(modalData);
    this.openModalFnMap[ModalTypeEnum.INFO] = (modalData) => this.openInfoModal(modalData);
    // this.openModalFnMap[ModalTypeEnum.LISTA_DIG_IFRAME] = (modalData) => this.openListaDigIFrameModal(modalData);
    this.openModalFnMap[ModalTypeEnum.GOOGLE_MAP] = (modalData) => this.openGoogleMapModal(modalData);
    // this.openModalFnMap[ModalTypeEnum.LISTA_DIG_DIRECT] = (modalData) => this.openListaDigDirectModal(modalData);
    // this.openModalFnMap[ModalTypeEnum.ARE_YOU_LISTED] = (modalData) => this.openAreYouListedModal(modalData);
    this.openModalFnMap[ModalTypeEnum.CUSTOMER_RATING] = (modalData) => this.openCustomerRatingModal(modalData);
    this.openModalFnMap[ModalTypeEnum.VARDEN_BOOKING_CONFIRMATION_V2] = (modalData) =>
      this.openVardenBookingConfirmationModalV2(modalData);
    this.openModalFnMap[ModalTypeEnum.TREATMENT_DETAILS] = (modalData) => this.openTreatmentDetailsModal(modalData);
    this.openModalFnMap[ModalTypeEnum.MESSAGE] = (modalData) => this.openMessageModal(modalData);
    this.openModalFnMap[ModalTypeEnum.GALLERY] = (modalData) => this.openGalleryModal(modalData);
  }

  private openVardenBookingConfirmationModalV2(modalData: BookingResponse): void {
    let modalConfig: MatDialogConfig = {
      panelClass: 'fullscreen-booking-modal',
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: '100%',
      width: '100%',
      data: modalData,
      autoFocus: false,
    };
    if (this.tools.getDeviceType() === DeviceTypeEnum.DESKTOP) {
      modalConfig = {
        panelClass: '',
        maxWidth: '100vw',
        width: '400px',
        data: modalData,
        autoFocus: false,
      };
    }
    const dialog: MatDialogRef<VardenBookingConfirmationV2Component> = this.dialog.open(
      VardenBookingConfirmationV2Component,
      modalConfig,
    );
    dialog.afterClosed().subscribe(() => this.onClose(ModalTypeEnum.VARDEN_BOOKING_CONFIRMATION_V2));
  }

  private openRatingModal(modalData: CareproviderUnitItem): void {
    const dialog: MatDialogRef<RateComponent> = this.dialog.open(RateComponent, {
      panelClass: '',
      maxWidth: '100vw',
      width: '800px',
      data: modalData,
      autoFocus: false,
    });
    dialog.afterClosed().subscribe(() => this.onClose(ModalTypeEnum.RATING));
  }

  private openCustomerRatingModal(modalData: string): void {
    const dialog: MatDialogRef<CustomerRateComponent> = this.dialog.open(CustomerRateComponent, {
      panelClass: '',
      maxWidth: '100vw',
      width: '800px',
      data: modalData,
      autoFocus: false,
    });
    dialog.afterClosed().subscribe(() => this.onClose(ModalTypeEnum.CUSTOMER_RATING));
  }

  private openQuestionModal(modalData: CareproviderUnitItem): void {
    const dialog: MatDialogRef<AskComponent> = this.dialog.open(AskComponent, {
      panelClass: 'no-scroll-dialog',
      maxWidth: '100vw',
      width: '600px',
      data: modalData,
    });
    dialog.afterClosed().subscribe(() => this.onClose(ModalTypeEnum.QUESTION));
  }

  private openBookingModal(modalData: BookingModalData): void {
    const dialog: MatDialogRef<BookComponent> = this.dialog.open(BookComponent, {
      panelClass: '',
      maxWidth: '100vw',
      width: '600px',
      data: modalData,
    });
    dialog.afterClosed().subscribe(() => this.onClose(ModalTypeEnum.BOOKING));
  }

  private openCustomBookingModal(modalData: CustomerFeature): void {
    const dialog: MatDialogRef<CustomBookComponent> = this.dialog.open(CustomBookComponent, {
      panelClass: 'no-scroll-dialog',
      maxWidth: '100vw',
      width: '400px',
      data: modalData,
    });
    dialog.afterClosed().subscribe(() => this.onClose(ModalTypeEnum.CUSTOM_BOOKING));
  }

  // private openAreYouListedModal(modalData: BookingModalData): void {
  //   const dialog: MatDialogRef<AreYouListedModalComponent> = this.dialog.open(AreYouListedModalComponent, {
  //     panelClass: '',
  //     maxWidth: '100vw',
  //     width: '400px',
  //     data: modalData,
  //   });
  //   dialog.afterClosed().subscribe(() => this.onClose(ModalTypeEnum.ARE_YOU_LISTED));
  // }

  private openUpdateInfoModal(modalData: CareproviderUnitItem): void {
    const dialog: MatDialogRef<UpdateInfoFormComponent> = this.dialog.open(UpdateInfoFormComponent, {
      maxWidth: '100vw',
      width: '600px',
      data: modalData,
    });
    dialog.afterClosed().subscribe(() => this.onClose(ModalTypeEnum.UPDATE_INFO));
  }

  private openDoktorseReceptModal(modalData: DLPageTypeEnum): void {
    const dialog: MatDialogRef<DoktorseReceptComponent> = this.dialog.open(DoktorseReceptComponent, {
      panelClass: 'no-scroll-dialog',
      maxWidth: '100vw',
      width: '600px',
      data: modalData,
    });
    dialog.afterClosed().subscribe(() => this.onClose(ModalTypeEnum.DOKTORSE_RECEPT));
  }

  // private openMailchimpModal(): void {
  //   const dialogPosition: DialogPosition = {
  //     bottom: '2vh',
  //     right: '2vh',
  //   };
  //
  //   this.scrollStrategy = this.sso.noop();
  //
  //   const dialog: MatDialogRef<MailchimpModalComponent> = this.dialog.open(MailchimpModalComponent, {
  //     panelClass: 'no-scroll-dialog',
  //     maxWidth: '100vw',
  //     width: '400px',
  //     position: dialogPosition,
  //     hasBackdrop: false,
  //     scrollStrategy: this.scrollStrategy,
  //   });
  //   dialog.afterClosed().subscribe(() => this.onClose(ModalTypeEnum.MAILCHIMP));
  // }

  private openBecomeCustomerModal(modalData: string): void {
    const dialog: MatDialogRef<BecomeCustomerFormComponent> = this.dialog.open(BecomeCustomerFormComponent, {
      panelClass: '',
      maxWidth: '100vw',
      width: '600px',
      data: modalData,
    });
    dialog.afterClosed().subscribe(() => this.onClose(ModalTypeEnum.BECOME_CUSTOMER));
  }

  // private openListaDigHsaModal(modalData: CareproviderUnitItem): void {
  //   const dialog: MatDialogRef<ListaDigHsaModalComponent> = this.dialog.open(ListaDigHsaModalComponent, {
  //     panelClass: 'no-scroll-dialog',
  //     maxWidth: '100vw',
  //     width: '600px',
  //     data: modalData,
  //   });
  //   dialog.afterClosed().subscribe(() => this.onClose(ModalTypeEnum.LISTA_DIG_HSA));
  // }

  private openInfoModal(modalData: InfoModalContent): void {
    const dialog: MatDialogRef<InfoModalComponent> = this.dialog.open(InfoModalComponent, {
      maxWidth: '100vw',
      width: !this.tools.isNil(modalData.width) ? `${modalData.width}px` : '400px',
      data: modalData,
    });
    dialog.afterClosed().subscribe(() => this.onClose(ModalTypeEnum.INFO));
  }

  private openTreatmentDetailsModal(modalData: TreatmentDetailsModalData): void {
    let modalConfig: MatDialogConfig = {
      panelClass: 'fullscreen-treatment-details-modal',
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: '100%',
      width: '100%',
      data: modalData,
      autoFocus: false,
    };
    if (this.tools.getDeviceType() === DeviceTypeEnum.DESKTOP) {
      modalConfig = {
        panelClass: 'fullscreen-treatment-details-modal',
        maxWidth: '100vw',
        maxHeight: '70vh',
        width: '720px',
        data: modalData,
        autoFocus: false,
      };
    }
    const dialog: MatDialogRef<TreatmentDetailsModalComponent> = this.dialog.open(
      TreatmentDetailsModalComponent,
      modalConfig,
    );
    dialog.afterClosed().subscribe(() => this.onClose(ModalTypeEnum.TREATMENT_DETAILS));
  }

  // private openListaDigIFrameModal(modalData: IFrameModalData): void {
  //   const dialog: MatDialogRef<IframeModalComponent> = this.dialog.open(IframeModalComponent, {
  //     panelClass: 'no-scroll-dialog',
  //     maxWidth: '100vw',
  //     width: '400px',
  //     data: modalData,
  //   });
  //   dialog.afterClosed().subscribe(() => this.onClose(ModalTypeEnum.LISTA_DIG_IFRAME));
  // }
  //
  // private openListaDigDirectModal(modalData: ListaDigVardenModalData): void {
  //   const dialogConfig = new MatDialogConfig();
  //   if (this.tools.getDeviceType() === DeviceTypeEnum.DESKTOP) {
  //     dialogConfig.width = '90%';
  //     dialogConfig.height = 'auto';
  //     dialogConfig.maxWidth = '624px';
  //     dialogConfig.panelClass = 'varden-lista-dig-modal';
  //   } else {
  //     dialogConfig.width = '100%';
  //     dialogConfig.height = '100%';
  //     dialogConfig.maxWidth = '100%';
  //     dialogConfig.maxHeight = '100%';
  //     dialogConfig.panelClass = ['varden-lista-dig-modal', 'varden-lista-dig-modal-mobile'];
  //   }
  //   dialogConfig.backdropClass = 'varden-lista-dig-modal-backdrop';
  //
  //   dialogConfig.data = {
  //     careProviderUnitId: modalData.careproviderUnitId,
  //     styling: {},
  //     initialScreen: 1,
  //   };
  //
  //   const dialog = this.dialog.open(ListaDigWidgetWrapperComponent, dialogConfig);
  //   dialog.afterClosed().subscribe(() => this.onClose(ModalTypeEnum.LISTA_DIG_DIRECT));
  // }

  private openGoogleMapModal(modalData: MapData): void {
    this.addEventListenerForDisablePinch(ModalTypeEnum.GOOGLE_MAP);
    const dialog: MatDialogRef<GoogleMapModalComponent> = this.dialog.open(GoogleMapModalComponent, {
      panelClass: 'google-map-dialog',
      maxWidth: '100vw',
      maxHeight: '100vh',
      data: modalData,
      autoFocus: false,
      disableClose: true,
    });
    dialog.afterClosed().subscribe(() => {
      this.onClose(ModalTypeEnum.GOOGLE_MAP);
      if (modalData.callback) {
        modalData.callback();
      }
    });
  }

  private openMessageModal(modalData: MessageModalData): void {
    const dialog: MatDialogRef<MessageModalComponent> = this.dialog.open(MessageModalComponent, {
      panelClass: 'px-3',
      maxWidth: '65ch',
      width: '100%',
      data: modalData,
    });
    dialog.afterClosed().subscribe(() => this.onClose(ModalTypeEnum.MESSAGE));
  }

  private openGalleryModal(modalData: ImagesCarouselData): void {
    const dialog: MatDialogRef<ImagesCarouselComponent> = this.dialog.open(ImagesCarouselComponent, {
      panelClass: '',
      maxWidth: '100vw',
      maxHeight: '100vh',
      width: '720px',
      data: modalData,
      autoFocus: false,
    });
    dialog.afterClosed().subscribe(() => this.onClose(ModalTypeEnum.GALLERY));
  }

  private addEventListenerForDisablePinch(type: ModalTypeEnum): void {
    document.addEventListener(
      'touchmove',
      (e) => {
        const modal = this.activeModals.find((t) => t === type);
        if (modal && e.touches.length > 1) {
          e.preventDefault();
        }
      },
      {passive: false},
    );
  }
}
